import { List, AutoSizer } from 'react-virtualized';
import type { User } from '@/configs/apis/user';
import EmptyIcon from '@/assets/images/common/svg/empty.svg?react';
import { getAvatar } from '@/scripts/utils/genAvatar';
import type { Contact, CreateContactParams } from '@/contexts/contacts/types';
import type {
  ChatChatId,
  ChatParams,
  CreateChatParams,
} from '@/contexts/chat/scripts/hooks/useChats/types';
import styles from './index.module.scss';
import OperationBar from './operation-bar';

export default function UserList({
  users,
  onCancel,
  addContacts,
  createChat,
  activeChat,
  contacts,
}: {
  users: User[];
  onCancel: () => void;
  addContacts: (contacts: CreateContactParams[]) => Promise<any>;
  createChat: (params: CreateChatParams) => Promise<void | ChatParams>;
  activeChat: (id: ChatChatId) => Promise<true | undefined>;
  contacts: Contact[];
}) {
  const onAdd = useCallback(
    async (user: User) => {
      const params: Contact = {
        id: 0,
        category: 0,
        user_id: user.userId,
        state_msg: '',
        name: user.name || '',
        username: user.username || '',
        avatar: user.avatar || '',
        remark_name: '',
        verified: 0,
        dnd: 0,
        org_id: 0,
        org_name: '',
        external: 0,
        role: 0,
        create_at: 0,
        update_at: 0,
      };
      const res = await createChat({ userId: params.user_id });
      if (res) {
        await addContacts([Object.assign(params, { chat_id: res.chat_id })]);
      }
    },
    [addContacts, createChat]
  );

  const rowRenderer = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const user = users[index];
    return (
      <div
        data-index={index}
        key={user.userId}
        style={style}
        className={styles.item}
        onClick={async () => {
          const res = await createChat({ userId: user.userId });
          if (res) {
            const isActivated = await activeChat(res.chat_id);
            if (isActivated) {
              onCancel();
            }
          }
        }}
      >
        <div className={styles.info}>
          <MyAvatar src={getAvatar(user.avatar, user.name)}></MyAvatar>
          <div className={styles.body}>
            <div className={styles.name}>{user.name}</div>
            {user.username && <div className={styles.shortName}>@{user.username}</div>}
          </div>
        </div>
        <OperationBar contacts={contacts} user={user} onAdd={onAdd} />
      </div>
    );
  };

  return (
    <div className={styles.content}>
      <AutoSizer>
        {({ width, height }) => (
          <List
            className="scrollbar_hover"
            width={width}
            height={height}
            rowHeight={60}
            rowCount={users.length}
            rowRenderer={rowRenderer}
            noRowsRenderer={() => (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}>
                  <EmptyIcon />
                </div>
                <div>There were no contacts, try a new search.</div>
              </div>
            )}
            scrollToIndex={0}
          />
        )}
      </AutoSizer>
    </div>
  );
}
